import { createReducer } from "@reduxjs/toolkit";
import { CountryISO } from "@ifgengineering/client-invest-sdk";
import { PlaidState } from "./plaid.types";
import {
  checkPlaidCoverageAPI,
  getPlaidLinkTokenAPI,
  getPaymentInitiationLinkTokenAPI,
  getIdvLinkTokenAPI,
  createBankAccountFromPlaidAPI,
  setSelectedCountry,
  resetPlaidState,
  resetVerificationStatus,
  clearPaymentInitiationLinkToken,
  clearIdvLinkToken,
} from "./plaid.actions";

const initialState: PlaidState = {
  coverageLoading: false,
  linkTokenLoading: false,
  verificationLoading: false,
  linkToken: null,
  linkTokenError: false,
  isSupported: null,
  selectedCountry: "GB" as CountryISO,
  verificationStatus: null,
  paymentInitiationLinkTokenLoading: false,
  paymentInitiationLinkToken: null,
  idvLinkTokenLoading: false,
  idvLinkToken: null,
  createBankAccountLoading: false,
  createdBankAccount: null,
  createBankAccountError: undefined,
};

export const plaidReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setSelectedCountry, (state, action) => {
      state.selectedCountry = action.payload;
      state.isSupported = null;
    })
    .addCase(resetPlaidState, () => {
      return initialState;
    })
    .addCase(resetVerificationStatus, (state) => {
      state.verificationStatus = null;
    })
    .addCase(checkPlaidCoverageAPI.pending, (state) => {
      state.coverageLoading = true;
    })
    .addCase(checkPlaidCoverageAPI.fulfilled, (state, action) => {
      state.coverageLoading = false;
      state.isSupported = action.payload.supported;
    })
    .addCase(checkPlaidCoverageAPI.rejected, (state, action) => {
      state.coverageLoading = false;
      state.isSupported = false;
    })
    .addCase(getPlaidLinkTokenAPI.pending, (state) => {
      state.linkTokenLoading = true;
      state.linkTokenError = false;
    })
    .addCase(getPlaidLinkTokenAPI.fulfilled, (state, action) => {
      state.linkTokenLoading = false;
      state.linkToken = action.payload.linkToken;
      state.linkTokenError = false;
    })
    .addCase(getPlaidLinkTokenAPI.rejected, (state) => {
      state.linkTokenLoading = false;
      state.linkToken = null;
      state.linkTokenError = true;
    })
    .addCase(getPaymentInitiationLinkTokenAPI.pending, (state) => {
      state.paymentInitiationLinkTokenLoading = true;
    })
    .addCase(getPaymentInitiationLinkTokenAPI.fulfilled, (state, action) => {
      state.paymentInitiationLinkTokenLoading = false;
      state.paymentInitiationLinkToken = action.payload.linkToken;
    })
    .addCase(getPaymentInitiationLinkTokenAPI.rejected, (state) => {
      state.paymentInitiationLinkTokenLoading = false;
      state.paymentInitiationLinkToken = null;
    })
    .addCase(clearPaymentInitiationLinkToken, (state) => {
      state.paymentInitiationLinkToken = null;
    })
    .addCase(getIdvLinkTokenAPI.pending, (state) => {
      state.idvLinkTokenLoading = true;
    })
    .addCase(getIdvLinkTokenAPI.fulfilled, (state, action) => {
      state.idvLinkTokenLoading = false;
      state.idvLinkToken = action.payload.linkToken;
    })
    .addCase(getIdvLinkTokenAPI.rejected, (state) => {
      state.idvLinkTokenLoading = false;
      state.idvLinkToken = null;
    })
    .addCase(clearIdvLinkToken, (state) => {
      state.idvLinkToken = null;
    })
    .addCase(createBankAccountFromPlaidAPI.pending, (state) => {
      state.createBankAccountLoading = true;
      state.createdBankAccount = null;
      state.createBankAccountError = undefined;
    })
    .addCase(createBankAccountFromPlaidAPI.fulfilled, (state, action) => {
      state.createBankAccountLoading = false;
      state.createdBankAccount = action.payload.bankAccount;
      state.createBankAccountError = undefined;
    })
    .addCase(createBankAccountFromPlaidAPI.rejected, (state, action) => {
      state.createBankAccountLoading = false;
      state.createdBankAccount = null;
      state.createBankAccountError =
        action.payload || "Failed to add bank account";
    });
});
